import { action, makeObservable, observable } from 'mobx'

export type ThemeMode = 'dark' | 'light'

/** Ключ у localStorage. Той самий рядок читає inline-скрипт в index.html до рендеру. */
export const THEME_STORAGE_KEY = 'autostrada.theme'

const THEME_COLOR: Record<ThemeMode, string> = { dark: '#1c1c1c', light: '#ffffff' }

function isThemeMode(value: unknown): value is ThemeMode {
    return value === 'dark' || value === 'light'
}

function readStoredMode(): ThemeMode {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
        return isThemeMode(stored) ? stored : 'dark'
    } catch {
        return 'dark'
    }
}

/**
 * Тема адмінки. Вибір живе в localStorage; атрибут data-theme на <html>
 * читають CSS-змінні в Ship/Styles/theme.css, а antd отримує алгоритм
 * через вкладений ConfigProvider у DefaultLayout.
 */
export class ThemeStore {
    mode: ThemeMode

    /** Режим, збережений користувачем; forceMode його не змінює */
    private savedMode: ThemeMode

    constructor() {
        this.savedMode = readStoredMode()
        this.mode = this.savedMode
        makeObservable(this, {
            mode: observable,
            setMode: action,
            forceMode: action,
            restoreMode: action,
        })
        this.applyToDocument(this.mode)
    }

    setMode(mode: ThemeMode): void {
        this.savedMode = mode
        this.mode = mode
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, mode)
        } catch {
            // приватний режим або заблоковані дані сайту: тема живе до перезавантаження
        }
        this.applyToDocument(mode)
    }

    toggle(): void {
        this.setMode(this.mode === 'dark' ? 'light' : 'dark')
    }

    /** Тимчасово примусити режим (кабінет водія завжди темний), не чіпаючи збережений вибір */
    forceMode(mode: ThemeMode): void {
        this.mode = mode
        this.applyToDocument(mode)
    }

    restoreMode(): void {
        this.mode = this.savedMode
        this.applyToDocument(this.savedMode)
    }

    private applyToDocument(mode: ThemeMode): void {
        if (typeof document === 'undefined') return
        document.documentElement.dataset['theme'] = mode
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[mode])
    }
}

export const themeStore = new ThemeStore()
